import {z} from "zod/v4-mini"
import type {Evaluator} from "@cm/graph-v3"
import {graphNode} from "@cm/graph-v3"
import {registerTemplateGraphNode, getTemplateNodeReturns, externalId, TypeDescriptors, visitNone} from "@cm/template-nodes"
import type {
    OnCompileContext,
    TemplateNodeContext,
    TemplateNodeDefinition,
    VisitorNodeResult,
    IGetInfoNodeParts,
    BuilderInlet,
    GraphBuilderScope,
    NodeEvaluator,
} from "@cm/template-nodes"
import type {MattressComponentInstance} from "#mattress/nodes/component/mattress-component-instance"
import {MattressOutput, type MattressOutputParameters} from "#mattress/nodes/mattress-output"
import type {MattressReferenceFrameData} from "#mattress/nodes/reference-frame/mattress-reference-frame"
import {
    mattressReferenceFrameToGlobalUVSpace,
    mattressReferenceFrameFromGlobalUVSpace,
    aliasReferenceMattressFrameData,
    resolveMattressReferenceFrameData,
    mattressReferenceFramesDataListsPush,
    getMattressCompileContext,
} from "#mattress/nodes/component/mattress-component-helpers"
import type {MattressNodePath} from "#mattress/nodes/component/mattress-component-types"
import {mattressReferenceFrameLikeMarker} from "#mattress/role-markers"
import type {MattressReferenceFrameLikeNode} from "#mattress/role-markers"

import type {Vector2Like} from "@cm/math"

const TD = TypeDescriptors

const mattressNodePathArrayDesc = TD.Array(TD.Identity<MattressNodePath[number]>())

export type MattressReferenceFrameOutputParameters = MattressOutputParameters

const definition: TemplateNodeDefinition<MattressReferenceFrameOutputParameters> = {
    nodeClass: "MattressReferenceFrameOutput",
    validation: {
        parameters: z.object({
            name: z.string(),
            component: graphNode<MattressComponentInstance>("MattressComponentInstance"),
            outputId: externalId,
        }),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class MattressReferenceFrameOutput extends MattressOutput implements MattressReferenceFrameLikeNode {
    get [mattressReferenceFrameLikeMarker]() {
        return true as const
    }

    public override get definition() {
        return definition
    }

    public override onCompile(
        context: OnCompileContext,
        _evaluator: Evaluator<TemplateNodeContext>,
    ): VisitorNodeResult<MattressReferenceFrameOutputParameters> {
        const mattressContext = getMattressCompileContext(context)
        const {evaluator} = context
        const {evaluatedData, nodePath} = mattressContext
        const {referenceFramesDataLists} = evaluatedData

        const scope = evaluator.getScope(this)
        const outputData = this.evaluateComponentOutput<MattressReferenceFrameData>("mattressReferenceFrame", scope, evaluator)

        const newNodePath = scope.pureLambda(scope.tuple(nodePath, this), ([nodePath, instance]) => [...nodePath, instance], "newPath", {
            output: mattressNodePathArrayDesc,
        })

        const referenceFrameData = scope.pureLambda(
            scope.tuple(outputData, newNodePath),
            ([outputData, newNodePath]) => {
                if (outputData === null) return null
                return {...outputData, nodePath: newNodePath}
            },
            "referenceFrameData",
        )

        mattressReferenceFramesDataListsPush(scope, referenceFramesDataLists, referenceFrameData)
        aliasReferenceMattressFrameData(this, evaluator, referenceFrameData)
        return visitNone(this.parameters)
    }

    toGlobalUVSpace(uv: Vector2Like, context: IGetInfoNodeParts): Vector2Like {
        return mattressReferenceFrameToGlobalUVSpace(this, uv, context)
    }

    fromGlobalUVSpace(uv: Vector2Like, context: IGetInfoNodeParts): Vector2Like {
        return mattressReferenceFrameFromGlobalUVSpace(this, uv, context)
    }

    evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderInlet<MattressReferenceFrameData | null> {
        return resolveMattressReferenceFrameData(this, scope, evaluator)
    }
}
